// src/utils/liveRepublish.ts
//
// Přepublikování živých výsledků ze SERVERU (fáze 4a, F3): sdílí ho Živé
// výsledky (oprava, smazání stanoviště, rozstřel po ukončení), tabule
// (rozstřel po ukončení – C5) a ukončení závodu (dozveřejnění výsledků
// přijatých během ukončování – F2b). Dřív byl jen v Živých výsledcích
// (republishFromServer).
//
// Publikace čte živé výsledky i stanoviště ze serveru (návrh fáze 1, bod
// 3.5) – místní mezipaměť může držet neúplný dokument a seznam stanovišť na
// obrazovce může být zastaralý; rundy si přečte publikace sama (fáze 2,
// bod 4.5). Bez stanovišť na serveru se nepublikuje.

import { doc, getDocFromServer } from "firebase/firestore";
import { db } from "../firebase";
import type { LiveResult, LiveStation } from "../types/liveScoring";
import { firebaseErrorMessage } from "./firebaseErrors";
import { loadLiveResultsFromServer, loadLiveStationsFromServer } from "./liveResults";
import { publishLiveResults, publishNoticeText, type PublishResult } from "./publishResults";
import { reportWarning } from "./reportError";

/** Publikace čte živé výsledky ze serveru – bez připojení nejde. */
export const PUBLISH_OFFLINE_MESSAGE = "Publikace potřebuje připojení k internetu.";

/** Stanoviště na serveru nejsou – publikace by sečetla všechny klíče výsledků. */
export const NO_SERVER_STATIONS_MESSAGE = "Závod nemá na serveru žádná stanoviště – nelze publikovat.";

/**
 * Hláška, když se živé výsledky pro publikaci nepodaří načíst ze serveru
 * (offline unavailable). Z místní mezipaměti se nepublikuje.
 */
export function serverLoadErrorText(err: unknown): string {
  const code = err && typeof err === "object" ? (err as { code?: unknown }).code : undefined;
  const offline =
    code === "unavailable" ||
    code === "deadline-exceeded" ||
    (typeof navigator !== "undefined" && navigator.onLine === false);
  return offline
    ? PUBLISH_OFFLINE_MESSAGE
    : `Živé výsledky se nepodařilo načíst ze serveru: ${firebaseErrorMessage(err)}`;
}

/** Živé výsledky a stanoviště ze serveru (pro publikaci). Offline skončí chybou. */
export async function loadPublishState(
  raceId: string
): Promise<{ results: { [sn: number]: LiveResult }; stations: LiveStation[] }> {
  const [results, stations] = await Promise.all([
    loadLiveResultsFromServer(raceId),
    loadLiveStationsFromServer(raceId),
  ]);
  return { results, stations };
}

/** Je závod ukončený na serveru? Offline podle `fallback` (stav na obrazovce). */
export async function raceFinishedOnServer(raceId: string, fallback: boolean): Promise<boolean> {
  try {
    const snap = await getDocFromServer(doc(db, "races", raceId));
    return snap.exists() && (snap.data() as { status?: unknown }).status === "finished";
  } catch {
    return fallback;
  }
}

export type RepublishOutcome =
  /** Nic se nepublikovalo (závod neběží jako ukončený, prázdný seznam). */
  | { status: "skipped" }
  | { status: "published"; result: PublishResult; notice: string | null }
  /** `reason` = česká hláška (offline, bez stanovišť, chyba zápisu). */
  | { status: "failed"; reason: string };

export interface RepublishParams {
  raceId: string;
  raceDate: unknown;
  /** Dílčí publikace jen těchto čísel (a jejich skupin shody). */
  startNumbers: number[];
  /** Publikuje správce (startovní listinu synchronizuje z profilů). */
  asStaff: boolean;
  /**
   * Publikovat jen u závodu, který je TEĎ ukončený (ověří se na serveru;
   * offline podle `finishedFallback`). Za běhu výsledky zveřejní ukončení.
   */
  onlyIfFinished?: boolean;
  finishedFallback?: boolean;
}

/**
 * Dílčí přepublikování ze serverového stavu. Nikdy nevyhodí – výsledek říká,
 * co se stalo (volající skládá hlášku).
 */
export async function republishFromServer(params: RepublishParams): Promise<RepublishOutcome> {
  const { raceId, startNumbers, asStaff } = params;
  if (!raceId || startNumbers.length === 0) return { status: "skipped" };
  if (params.onlyIfFinished !== false && !(await raceFinishedOnServer(raceId, params.finishedFallback === true))) {
    return { status: "skipped" };
  }
  let server: { results: { [sn: number]: LiveResult }; stations: LiveStation[] };
  try {
    server = await loadPublishState(raceId);
  } catch (e) {
    reportWarning("liveRepublish.load", e, { raceId });
    return { status: "failed", reason: serverLoadErrorText(e) };
  }
  if (server.stations.length === 0) return { status: "failed", reason: NO_SERVER_STATIONS_MESSAGE };
  try {
    const result = await publishLiveResults({
      raceId,
      raceDate: params.raceDate,
      liveResults: server.results,
      stations: server.stations,
      onlyStartNumbers: startNumbers,
      asStaff,
    });
    return { status: "published", result, notice: publishNoticeText(result, asStaff) };
  } catch (e) {
    reportWarning("liveRepublish.publish", e, { raceId });
    return { status: "failed", reason: e instanceof Error ? e.message : String(e) };
  }
}

/** Body stanovišť (a rány – počet zásahů 4b) a rozstřel výsledku (pro porovnání dvou čtení). */
function resultSignature(result: LiveResult | undefined, stationIds: ReadonlyArray<string>): string {
  // Chybějící dokument = bez bodů (nový prázdný dokument publikaci nemění)
  const stations = ((result && result.stations) || {}) as Record<string, { score?: unknown; shots?: unknown } | null | undefined>;
  const scores = stationIds.map((id) => {
    const entry = stations[id];
    const score = entry && typeof entry === "object" ? entry.score : undefined;
    const shots = entry && typeof entry === "object" && Array.isArray(entry.shots) ? entry.shots.join(" ") : "";
    return typeof score === "number" && Number.isFinite(score) ? `${score}:${shots}` : "-";
  });
  const tb = result ? (result as { tiebreaker?: unknown }).tiebreaker : undefined;
  return `${scores.join(",")}|${typeof tb === "number" && tb > 0 ? tb : 0}`;
}

/**
 * Startovní čísla, jejichž výsledek se mezi dvěma čteními ze serveru změnil
 * (body nastavených stanovišť, rozstřel, nový / smazaný dokument) – po
 * ukončení závodu se dozveřejní (F2b).
 */
export function changedLiveResults(
  before: { [sn: number]: LiveResult },
  after: { [sn: number]: LiveResult },
  stationIds: ReadonlyArray<string>
): number[] {
  const numbers = new Set<number>();
  Object.keys(before).forEach((k) => numbers.add(Number(k)));
  Object.keys(after).forEach((k) => numbers.add(Number(k)));
  return Array.from(numbers)
    .filter((sn) => Number.isFinite(sn))
    .filter((sn) => resultSignature(before[sn], stationIds) !== resultSignature(after[sn], stationIds))
    .sort((a, b) => a - b);
}
